import React from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchInput, TouchSelect, TouchFormGroup, TouchCheckbox } from '../ui/TouchForm';

const RatingFilter = ({ 
  minRating,
  onMinRatingChange,
  minReviewCount,
  onMinReviewCountChange,
  hasPhotos,
  onHasPhotosChange,
  hasReviews,
  onHasReviewsChange,
  verifiedOnly,
  onVerifiedOnlyChange
}) => {
  const StarRatingSelector = ({ value, onChange, label }) => (
    <TouchFormGroup>
      <label style={{ 
        fontSize: '14px', 
        fontWeight: '600', 
        marginBottom: '8px',
        display: 'block'
      }}>
        {label}
      </label>
      
      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => onChange(value === star ? null : star)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: value && value >= star ? '#FFD700' : '#e0e0e0',
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 150ms ease'
            }}
            onMouseEnter={(e) => {
              if (value !== star) {
                e.target.style.color = '#FFC107';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.color = value && value >= star ? '#FFD700' : '#e0e0e0';
            }}
          >
            ★
          </button>
        ))}
        <span style={{ 
          marginLeft: '8px',
          fontSize: '14px',
          color: '#666',
          alignSelf: 'center'
        }}>
          {value ? `${value}+ stars` : 'Any rating'}
        </span>
      </div>
    </TouchFormGroup>
  );

  const reviewCountOptions = [
    { value: null, label: 'Any number of reviews' },
    { value: 1, label: 'At least 1 review' },
    { value: 5, label: 'At least 5 reviews' },
    { value: 10, label: 'At least 10 reviews' },
    { value: 25, label: 'At least 25 reviews' },
    { value: 50, label: 'At least 50 reviews' },
    { value: 100, label: 'At least 100 reviews' }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Minimum Star Rating */}
      <StarRatingSelector
        value={minRating}
        onChange={onMinRatingChange}
        label="⭐ Minimum Star Rating:"
      />

      {/* Quick Rating Buttons */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          🚀 Quick Rating Filters:
        </label>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {[
            { rating: 5, label: '5⭐ Excellent', color: '#4CAF50' },
            { rating: 4, label: '4⭐+ Very Good', color: '#8BC34A' },
            { rating: 3, label: '3⭐+ Good', color: '#FF9800' },
            { rating: 2, label: '2⭐+ Fair', color: '#FF5722' }
          ].map(option => (
            <TouchButton
              key={option.rating}
              onClick={() => onMinRatingChange(minRating === option.rating ? null : option.rating)}
              variant={minRating === option.rating ? 'primary' : 'outline'}
              size="small"
              style={{ 
                fontSize: '12px',
                borderColor: option.color,
                color: minRating === option.rating ? 'white' : option.color
              }}
            >
              {option.label}
            </TouchButton>
          ))}
        </div>
      </TouchFormGroup>

      {/* Minimum Review Count */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '8px',
          display: 'block'
        }}>
          📊 Minimum Number of Reviews:
        </label>
        
        <TouchSelect
          value={minReviewCount || ''}
          onChange={(e) => onMinReviewCountChange(e.target.value ? parseInt(e.target.value) : null)}
        >
          {reviewCountOptions.map(option => (
            <option key={option.value || 'null'} value={option.value || ''}>
              {option.label}
            </option>
          ))}
        </TouchSelect>
      </TouchFormGroup>

      {/* Review Content Filters */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          📝 Review Content:
        </label>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Has Reviews Toggle */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <TouchButton
              onClick={() => onHasReviewsChange(hasReviews === true ? null : true)}
              variant={hasReviews === true ? 'primary' : 'outline'}
              size="small"
              style={{ flex: 1, fontSize: '12px' }}
            >
              📝 Has Reviews
            </TouchButton>
            
            <TouchButton
              onClick={() => onHasReviewsChange(hasReviews === false ? null : false)}
              variant={hasReviews === false ? 'primary' : 'outline'}
              size="small"
              style={{ flex: 1, fontSize: '12px' }}
            >
              📄 No Reviews
            </TouchButton>
          </div>

          {/* Has Photos Toggle */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <TouchButton
              onClick={() => onHasPhotosChange(hasPhotos === true ? null : true)}
              variant={hasPhotos === true ? 'primary' : 'outline'}
              size="small"
              style={{ flex: 1, fontSize: '12px' }}
            >
              📸 With Photos
            </TouchButton>
            
            <TouchButton
              onClick={() => onHasPhotosChange(hasPhotos === false ? null : false)}
              variant={hasPhotos === false ? 'primary' : 'outline'}
              size="small"
              style={{ flex: 1, fontSize: '12px' }}
            >
              📷 No Photos
            </TouchButton>
          </div>
        </div>
      </TouchFormGroup>

      {/* Verification Status */}
      <TouchFormGroup>
        <TouchCheckbox
          label="✅ Verified Locations Only"
          checked={verifiedOnly}
          onChange={(e) => onVerifiedOnlyChange(e.target.checked)}
        />
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          Show only locations that have been verified by moderators or trusted sources
        </div>
      </TouchFormGroup>

      {/* Rating Quality Presets */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          🏆 Quality Presets:
        </label>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <TouchButton
            onClick={() => {
              onMinRatingChange(4);
              onMinReviewCountChange(10);
              onHasPhotosChange(true);
              onVerifiedOnlyChange(true);
            }}
            variant="outline"
            size="small"
            style={{ 
              fontSize: '12px',
              flexDirection: 'column',
              gap: '4px',
              height: '60px',
              padding: '8px'
            }}
          >
            <span>🥇</span>
            <span>Premium</span>
          </TouchButton>
          
          <TouchButton
            onClick={() => {
              onMinRatingChange(3);
              onMinReviewCountChange(5);
              onHasPhotosChange(true);
              onVerifiedOnlyChange(false);
            }}
            variant="outline"
            size="small"
            style={{ 
              fontSize: '12px',
              flexDirection: 'column',
              gap: '4px',
              height: '60px',
              padding: '8px'
            }}
          >
            <span>🥈</span>
            <span>Standard</span>
          </TouchButton>
          
          <TouchButton
            onClick={() => {
              onMinRatingChange(2);
              onMinReviewCountChange(1);
              onHasPhotosChange(null);
              onVerifiedOnlyChange(false);
            }}
            variant="outline"
            size="small"
            style={{ 
              fontSize: '12px',
              flexDirection: 'column',
              gap: '4px',
              height: '60px',
              padding: '8px'
            }}
          >
            <span>🥉</span>
            <span>Basic</span>
          </TouchButton>
          
          <TouchButton
            onClick={() => {
              onMinRatingChange(null);
              onMinReviewCountChange(null);
              onHasPhotosChange(null);
              onVerifiedOnlyChange(false);
            }}
            variant="outline"
            size="small"
            style={{ 
              fontSize: '12px',
              flexDirection: 'column',
              gap: '4px',
              height: '60px',
              padding: '8px'
            }}
          >
            <span>🌍</span>
            <span>All</span>
          </TouchButton>
        </div>
      </TouchFormGroup>

      {/* Popular Rating Combinations */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          🔥 Popular Combinations:
        </label>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <TouchButton
            onClick={() => {
              onMinRatingChange(4);
              onHasPhotosChange(true);
            }}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
          >
            🌟 Highly Rated + Photos
          </TouchButton>
          
          <TouchButton
            onClick={() => {
              onMinReviewCountChange(25);
              onVerifiedOnlyChange(true);
            }}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
          >
            📊 Well-Reviewed + Verified
          </TouchButton>
          
          <TouchButton
            onClick={() => {
              onMinRatingChange(3);
              onMinReviewCountChange(5);
            }}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
          >
            ⚖️ Balanced Quality
          </TouchButton>
          
          <TouchButton
            onClick={() => {
              onHasReviewsChange(false);
              onVerifiedOnlyChange(true);
            }}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
          >
            🆕 New + Verified
          </TouchButton>
        </div>
      </TouchFormGroup>

      {/* Active Filters Summary */}
      {(minRating || minReviewCount || hasPhotos !== null || hasReviews !== null || verifiedOnly) && (
        <div style={{
          padding: '12px',
          backgroundColor: '#f3e5f5',
          borderRadius: '8px',
          border: '1px solid #9C27B0'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#7B1FA2', marginBottom: '6px' }}>
            Active Rating Filters:
          </div>
          <div style={{ fontSize: '12px', color: '#333' }}>
            {minRating && (
              <div>Minimum Rating: {minRating}+ stars</div>
            )}
            {minReviewCount && (
              <div>Minimum Reviews: {minReviewCount}+</div>
            )}
            {hasPhotos === true && <div>Must have photos</div>}
            {hasPhotos === false && <div>No photos required</div>}
            {hasReviews === true && <div>Must have reviews</div>}
            {hasReviews === false && <div>No reviews yet</div>}
            {verifiedOnly && <div>Verified locations only</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingFilter;